import { API_BASE_URL } from '../../../config';
import type { Pin, CreatePinData, UpdatePinData, PinsResponse } from '../types';

const API_BASE = `${API_BASE_URL}/api/v1/locations`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Fetch all pins for the authenticated user */
export async function fetchPins(): Promise<Pin[]> {
  const response = await fetch(`${API_BASE}/pins`, {
    headers: getAuthHeaders(),
  });
  const data = await handleResponse<PinsResponse>(response);
  return data.pins;
}

/** Fetch a single pin by ID */
export async function fetchPin(pinId: string): Promise<Pin> {
  const response = await fetch(`${API_BASE}/pins/${pinId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<Pin>(response);
}

/** Create a new pin */
export async function createPin(data: CreatePinData): Promise<Pin> {
  const response = await fetch(`${API_BASE}/pins`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Pin>(response);
}

/** Update an existing pin */
export async function updatePin(pinId: string, data: UpdatePinData): Promise<Pin> {
  const response = await fetch(`${API_BASE}/pins/${pinId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Pin>(response);
}

/** Delete a pin */
export async function deletePin(pinId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/pins/${pinId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Delete failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
}
